import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { RootState } from "../store";
import { consultOrderActions } from "../store/consultOrder-slice";
import { selectHasSupportedPharmacistCapability } from "../store/auth-slice";
import {
  selectConsultPharmacyDetail,
  selectConsultPharmacyPair,
} from "../store/consultPharmacyContext-slice";
import { getTokenUserId } from "../utils/tokenUtils";
import { useConsultOrderContext } from "../contexts/ConsultOrderContext";
import {
  getConsultPrefill,
  createConsultOrder,
} from "../services/consultOrderService";
import {
  createPharmacistRfzoTherapy,
  orderRfzoTherapy,
} from "../services/consultRfzoService";
import {
  buildConsultOrderPayload,
  buildRfzoOrderPayload,
  validateConsultOrderDraft,
  ConsultOrderValidationErrors,
} from "../utils/consultOrderPayload";
import {
  buildConsultRfzoCreatePayload,
  validateConsultRfzoDraft,
  someRfzoItemInStock,
  ConsultRfzoSubmitMode,
  ConsultRfzoValidationErrors,
} from "../utils/consultRfzoPayload";
import { mapConsultSubmitError } from "../utils/consultOrderErrors";
import { isConsultDraftUnfinished } from "../utils/consultDraftState";
import {
  mapRfzoCreateError,
  mapRfzoOrderError,
} from "../utils/consultRfzoErrors";
import { ConsultOrderPrefillResponse } from "../types/consultOrder.types";
import { ConsultRfzoTherapyResponse } from "../types/consultRfzo.types";
import { CnCOrderResponse } from "../types/api";
import {
  CONSULT_ORDER_GRACE_MS,
  KEEPALIVE_404_THRESHOLD,
  KEEPALIVE_POLL_INTERVAL_MS,
  MOUNT_PROBE_INTERVAL_MS,
  MOUNT_PROBE_MAX_MS,
  TRIGGER_POLL_INTERVAL_MS,
  TRIGGER_POLL_MAX_MS,
} from "../utils/consultOrder.constants";

// Lifecycle state machine for the ITAM-2583 consult order. Owns ALL lifecycle
// state (phase, per-consult dedupe, ordered set, grace timer, generation
// counters, AbortControllers) and drives three bounded, cancellable polls:
//   1. Trigger poll  (after Join)  -> open the confirmation once on first 200.
//   2. Mount probe   (refresh)     -> show the chip on 200, NEVER auto-open.
//   3. Keep-alive    (modal open)  -> detect 200->404 (debounced) -> grace/clear.

export type ConsultOrderPhase =
  | "IDLE"
  | "ARMING"
  | "PROMPT"
  | "CHIP"
  | "FORM"
  | "SUBMITTING"
  | "SUCCESS"
  | "RFZO_FORM"
  | "RFZO_SUBMITTING"
  | "RFZO_RESULT";

/** Which submit produced the SUCCESS screen (null defaults to the CnC copy). */
export type ConsultSuccessKind = "CNC" | "RFZO";

export interface UseConsultOrderLifecycle {
  phase: ConsultOrderPhase;
  /** Latest fetched prefill (drives the prompt + is hydrated into the draft on confirm). */
  prefill: ConsultOrderPrefillResponse | null;
  chipVisible: boolean;
  graceActive: boolean;
  consultEnded: boolean;
  createdOrder: CnCOrderResponse | null;
  submitError: string | null;
  validationErrors: ConsultOrderValidationErrors;
  /** Increments on each failed submit attempt so the form can focus the first invalid field. */
  submitAttemptId: number;
  lineErrors: Record<number, string>;
  // --- ITAM-2614 RFZO state -----------------------------------------------
  /** 201 result of pharmacist_create — the in-session resume anchor. */
  rfzoResult: ConsultRfzoTherapyResponse | null;
  /** Which submit produced `rfzoResult` — the result screen's copy depends on it. */
  rfzoResultMode: ConsultRfzoSubmitMode | null;
  rfzoValidationErrors: ConsultRfzoValidationErrors;
  rfzoSubmitError: string | null;
  /** Increments on each failed submitRfzo attempt (focus-first-invalid). */
  rfzoSubmitAttemptId: number;
  /** Non-null while the shared form modal is in RFZO order mode (the discriminator). */
  rfzoOrderTherapyId: number | null;
  successKind: ConsultSuccessKind | null;
  /** Per-consult flags that drive the prompt's "Nastavi ..." labels. */
  cncFormVisited: boolean;
  rfzoFormVisited: boolean;
  openPrompt: () => void; // CHIP -> PROMPT (chip reopens the two-choice prompt)
  dismissPrompt: () => void; // PROMPT -> CHIP (decline; no re-prompt)
  confirmPrompt: () => void; // PROMPT -> FORM ("Rezervacija"; hydrates + clears the RFZO discriminator)
  closeForm: () => void; // FORM -> CHIP (PRESERVE the draft; leaves RFZO order mode)
  closeSuccess: () => void; // SUCCESS -> IDLE (clear the draft)
  submit: () => Promise<void>; // branches on rfzoOrderTherapyId (CnC vs RFZO order)
  dismissSubmitError: () => void;
  confirmRfzo: () => void; // PROMPT -> RFZO_FORM (hydrate; only while no result exists)
  resumeRfzo: () => void; // PROMPT -> RFZO_RESULT (requires a result)
  closeRfzoForm: () => void; // RFZO_FORM -> CHIP (draft preserved)
  closeRfzoResult: () => void; // RFZO_RESULT -> CHIP (or IDLE when ended + terminal)
  submitRfzo: (mode?: ConsultRfzoSubmitMode) => Promise<void>;
  startRfzoOrder: () => void; // RFZO_RESULT -> FORM in RFZO order mode (D9-gated)
  dismissRfzoSubmitError: () => void;
}

export const useConsultOrderLifecycle = (): UseConsultOrderLifecycle => {
  const dispatch = useDispatch();
  // Read directly inside async poll continuations (getState is synchronous
  // with dispatch) — a ref mirrored via an effect would lag one render flush
  // behind, and those continuations can run inside exactly that gap.
  const reduxStore = useStore<RootState>();
  const ctx = useConsultOrderContext();
  const { hydrate, clearDraft } = ctx;

  const armedConsult = useSelector(
    (s: RootState) => s.consultOrder.armedConsult
  );
  const token = useSelector((s: RootState) => s.auth.token);
  // ITAM-2622 (3.4): the CONSULT pharmacy context — the atomic pair from the
  // adviser profile — replaces the retail marketplace everywhere in this flow.
  // Retail surfaces keep reading auth marketplace state untouched, which is
  // what keeps the dual-role retail board unchanged.
  const consultPharmacy = useSelector(selectConsultPharmacyPair);
  const consultPharmacyDetail = useSelector(selectConsultPharmacyDetail);
  const hasPharmacistCapability = useSelector(
    selectHasSupportedPharmacistCapability
  );

  const consultantId = token ? getTokenUserId(token) : null;
  // ITAM-2622: no `takesOrders` term anywhere (decision 10, BF-8) — consult
  // availability is independent of the retail order board. Identical for the
  // consult-only and the dual-role persona.
  const eligible =
    consultantId != null &&
    hasPharmacistCapability &&
    consultPharmacy != null;

  const marketplaceKey = consultPharmacy
    ? `${consultPharmacy.marketplaceId}:${consultPharmacy.branchMarketplaceId}`
    : null;

  const [phase, setPhase] = useState<ConsultOrderPhase>("IDLE");
  const [prefill, setPrefill] = useState<ConsultOrderPrefillResponse | null>(
    null
  );
  const [orderedConsultIds, setOrderedConsultIds] = useState<number[]>([]);
  const [createdOrder, setCreatedOrder] = useState<CnCOrderResponse | null>(
    null
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] =
    useState<ConsultOrderValidationErrors>({});
  const [submitAttemptId, setSubmitAttemptId] = useState(0);
  const [lineErrors, setLineErrors] = useState<Record<number, string>>({});
  const [graceActive, setGraceActive] = useState(false);
  const [consultEnded, setConsultEnded] = useState(false);

  // --- ITAM-2614 RFZO state -------------------------------------------------
  const [rfzoResult, setRfzoResult] =
    useState<ConsultRfzoTherapyResponse | null>(null);
  const [rfzoResultMode, setRfzoResultMode] =
    useState<ConsultRfzoSubmitMode | null>(null);
  const [rfzoValidationErrors, setRfzoValidationErrors] =
    useState<ConsultRfzoValidationErrors>({});
  const [rfzoSubmitError, setRfzoSubmitError] = useState<string | null>(null);
  const [rfzoSubmitAttemptId, setRfzoSubmitAttemptId] = useState(0);
  const [rfzoOrderTherapyId, setRfzoOrderTherapyId] = useState<number | null>(
    null
  );
  const [successKind, setSuccessKind] = useState<ConsultSuccessKind | null>(
    null
  );
  const [cncFormVisited, setCncFormVisited] = useState(false);
  const [rfzoFormVisited, setRfzoFormVisited] = useState(false);

  // Refs read inside timers to avoid stale closures / extra effect deps.
  const phaseRef = useRef(phase);
  const orderedRef = useRef<Set<number>>(new Set());
  const genRef = useRef(0);
  const promptedRef = useRef<number | null>(null);
  const keepAlive404Ref = useRef(0);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submitAbortRef = useRef<AbortController | null>(null);
  const draftRef = useRef(ctx.draft);
  const rfzoDraftRef = useRef(ctx.rfzoDraft);
  const ctxPrefillRef = useRef(ctx.prefill);
  // The RFZO mirrors below are updated SYNCHRONOUSLY at their write sites (not
  // via a passive effect like phaseRef) so a same-tick read in submit()/guards
  // can never see a stale value.
  const rfzoResultRef = useRef<ConsultRfzoTherapyResponse | null>(null);
  const rfzoOrderTherapyIdRef = useRef<number | null>(null);
  /** ConsultId all per-consult UI state (result, visited flags) belongs to. */
  const stateConsultIdRef = useRef<number | null>(null);
  /**
   * Synchronous re-entry lock shared by submitRfzo and the RFZO branch of
   * submit(). The CnC phaseRef guard is EFFECT-updated, which leaves a
   * same-tick double-fire window — this ref closes it (set before any await,
   * cleared in finally).
   */
  const submitLockRef = useRef(false);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  useEffect(() => {
    orderedRef.current = new Set(orderedConsultIds);
  }, [orderedConsultIds]);
  useEffect(() => {
    draftRef.current = ctx.draft;
    rfzoDraftRef.current = ctx.rfzoDraft;
    ctxPrefillRef.current = ctx.prefill;
  });

  const clearGraceTimer = useCallback(() => {
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);

  /**
   * ITAM-2614: clear every piece of per-consult RFZO/UI state. Called from
   * resetToIdle and from the consult-keying guards (arm effect / mount probe)
   * when the active consultId changes — without the latter, patient A's
   * orderable therapy could surface (and be ordered!) inside patient B's
   * consult.
   */
  const clearRfzoState = useCallback(() => {
    stateConsultIdRef.current = null;
    rfzoResultRef.current = null;
    rfzoOrderTherapyIdRef.current = null;
    setRfzoResult(null);
    setRfzoResultMode(null);
    setRfzoOrderTherapyId(null);
    setSuccessKind(null);
    setCncFormVisited(false);
    setRfzoFormVisited(false);
    setRfzoSubmitError(null);
    setRfzoValidationErrors({});
  }, []);

  const resetToIdle = useCallback(() => {
    clearGraceTimer();
    genRef.current += 1; // invalidate any in-flight poll
    promptedRef.current = null;
    keepAlive404Ref.current = 0;
    if (submitAbortRef.current) {
      submitAbortRef.current.abort();
      submitAbortRef.current = null;
    }
    setConsultEnded(false);
    setGraceActive(false);
    setPrefill(null);
    setSubmitError(null);
    setValidationErrors({});
    setLineErrors({});
    setCreatedOrder(null);
    clearRfzoState();
    clearDraft();
    setPhase("IDLE");
    dispatch(consultOrderActions.clearArmed());
  }, [clearGraceTimer, clearRfzoState, clearDraft, dispatch]);

  const onConsultInactive = useCallback(() => {
    // ITAM-2622 (3.6a): the SAME rule the logout gate uses — one function
    // decides both whether the grace window is worth opening and whether logout
    // must warn. A draft is "started" once the form has been entered (context
    // prefill is hydrated); it stays started after the form is closed back to
    // the chip, so it must never be silently destroyed on consult end.
    //
    // This caller keeps reading REFS, and that is not a contradiction with the
    // reactive publisher below: `onConsultInactive` is a useCallback with deps
    // [clearGraceTimer, resetToIdle], invoked from a poll continuation, and the
    // keep-alive effect holds one instance until its identity changes. Feeding
    // it reactive values without touching its dep list would freeze them at the
    // render that created the callback. Shared *rule*, per-caller *inputs*.
    //
    // The refactor is behavior-preserving on every input this caller can
    // actually receive: its one call site sits inside the keep-alive tick,
    // whose active set excludes IDLE, ARMING, SUCCESS and a terminal
    // RFZO_RESULT — exactly the phases whose classification changed.
    if (
      isConsultDraftUnfinished(
        phaseRef.current,
        ctxPrefillRef.current != null,
        rfzoResultRef.current?.orderable === true
      )
    ) {
      // Grace window: keep the draft submittable, block a NEW draft (the arm
      // effect only fires from IDLE/CHIP), show a banner. Phase is left as-is
      // so a chip-closed draft can still be reopened and sent during grace.
      clearGraceTimer();
      setConsultEnded(true);
      setGraceActive(true);
      graceTimerRef.current = setTimeout(() => {
        resetToIdle();
      }, CONSULT_ORDER_GRACE_MS);
    } else {
      // No started draft (bare PROMPT/CHIP from a probe) -> just clear.
      resetToIdle();
    }
  }, [clearGraceTimer, resetToIdle]);

  // --- Arm reaction: Join signal -> ARMING (only from IDLE/CHIP) ------------
  useEffect(() => {
    const id = armedConsult?.consultId;
    if (id == null) {
      return;
    }
    if (orderedRef.current.has(id)) {
      return; // FE one-order-per-consult guard
    }
    if (phaseRef.current === "IDLE" || phaseRef.current === "CHIP") {
      // A new consult must never inherit the PREVIOUS consult's end-of-consult
      // state. Arming from CHIP is exactly where a grace-parked consult sits
      // (onConsultInactive leaves the phase alone and arms a 15-minute
      // resetToIdle): left running, that timer would fire mid-way through the
      // new consult and wipe its draft, and a stale consultEnded would keep
      // keep-alive switched off (so the NEW consult's end is never detected)
      // while showing its forms the "consult is over" banner from the start.
      clearGraceTimer();
      setConsultEnded(false);
      setGraceActive(false);
      // Consult-keying (ITAM-2614) is NOT a destructive wipe here: the state
      // may belong to this very consult (a re-arm), and wiping would strip
      // the "Rezervacija" disable of an already-persisted therapy. Instead the
      // trigger poll IGNORES prefills for any consult other than the armed
      // one (a lagging backend can still return the previous consult) and
      // re-keys — clearing the other consult's RFZO state and context draft —
      // only when the armed consult's prefill actually settles.
      promptedRef.current = null;
      setPrefill(null);
      setPhase("ARMING");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armedConsult?.consultId]);

  // --- Trigger poll (phase ARMING) -----------------------------------------
  // ITAM-2622: `eligible` gates the ARMED -> polling transition too. Before
  // that, an armed consult started prefill polling on `consultantId` alone,
  // which would fire consult requests for a session with no pharmacy context.
  useEffect(() => {
    if (phase !== "ARMING" || consultantId == null || !eligible) {
      return;
    }
    let cancelled = false;
    let settled = false;
    let busy = false; // in-flight guard: never overlap poll requests
    let inFlight: AbortController | null = null;
    const gen = ++genRef.current;
    let elapsed = 0;

    const tick = async () => {
      if (cancelled || settled || busy) {
        return;
      }
      busy = true;
      const controller = new AbortController();
      inFlight = controller;
      try {
        const result = await getConsultPrefill(consultantId, controller.signal);
        if (cancelled || settled || gen !== genRef.current) {
          return;
        }
        if (result) {
          // Consult-keying (ITAM-2614): the arm signal names the consult the
          // pharmacist actually joined, and a lagging backend can still
          // return the PREVIOUS consult here. Adopting it would resurface
          // patient A's prompt — and their RFZO resume path — inside patient
          // B's consultation, so a mismatched prefill is ignored (nothing
          // renders during ARMING) and polling continues until the armed
          // consult surfaces or the window closes. The armed id is read LIVE
          // from the store: a re-arm dispatched while this request was in
          // flight is visible to this continuation immediately, before any
          // render/effect flush.
          const armedId =
            reduxStore.getState().consultOrder.armedConsult?.consultId ?? null;
          if (armedId != null && result.consultId !== armedId) {
            return;
          }
          settled = true;
          if (orderedRef.current.has(result.consultId)) {
            resetToIdle();
            return;
          }
          // Per-consult state is re-keyed at SETTLE time, not wiped at arm
          // time: a re-armed same consult keeps its state (and with it the
          // "Rezervacija" disable), while a different consult starts clean.
          if (
            stateConsultIdRef.current != null &&
            stateConsultIdRef.current !== result.consultId
          ) {
            clearRfzoState();
          }
          // The context draft (caller PII + the "started draft" grace signal)
          // is per-consult too: left in place, patient A's draft would grant
          // patient B's bare chip the 15-minute grace protection on consult
          // end — and A's PII would seed B's forms.
          if (
            ctxPrefillRef.current != null &&
            ctxPrefillRef.current.consultId !== result.consultId
          ) {
            clearDraft();
          }
          setPrefill(result);
          if (promptedRef.current !== result.consultId) {
            promptedRef.current = result.consultId; // auto-open ONCE
            setPhase("PROMPT");
          } else {
            setPhase("CHIP");
          }
        }
        // 404 -> keep polling until the deadline.
      } catch {
        // transient error -> keep polling until the deadline.
      } finally {
        busy = false;
        if (inFlight === controller) {
          inFlight = null;
        }
      }
    };

    tick();
    const interval = setInterval(() => {
      elapsed += TRIGGER_POLL_INTERVAL_MS;
      if (elapsed >= TRIGGER_POLL_MAX_MS) {
        cancelled = true;
        clearInterval(interval);
        if (inFlight) {
          inFlight.abort();
        }
        if (phaseRef.current === "ARMING") {
          // Exhausted with no adoptable 200. KNOWN LIMITATION: if the
          // backend kept returning the PREVIOUS consult for the whole
          // window, this reset drops the armed id, and the mount probe that
          // follows has no armed context — a still-lagging backend can then
          // surface the previous consult's CHIP (never auto-opens). That
          // needs a lag longer than TRIGGER_POLL_MAX_MS, and the pre-fix
          // behavior (instant adoption with an auto-opened prompt) was
          // strictly worse; no extra machinery for it by design.
          resetToIdle();
        }
        return;
      }
      tick();
    }, TRIGGER_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (inFlight) {
        inFlight.abort();
      }
    };
    // The armed id is a dep so a re-arm DURING ARMING (the arm effect
    // declines outside IDLE/CHIP) restarts the poll: fresh 2-minute window
    // for the new consult, stale in-flight request aborted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, eligible, consultantId, armedConsult?.consultId]);

  // --- Mount probe (phase IDLE, eligible, not armed) -----------------------
  useEffect(() => {
    if (phase !== "IDLE" || !eligible || consultantId == null) {
      return;
    }
    if (armedConsult?.consultId != null) {
      return; // the arm path will handle it
    }
    let cancelled = false;
    let settled = false;
    let busy = false; // in-flight guard: never overlap probe requests
    let inFlight: AbortController | null = null;
    const gen = ++genRef.current;
    let elapsed = 0;

    const probe = async () => {
      if (cancelled || settled || busy) {
        return;
      }
      busy = true;
      const controller = new AbortController();
      inFlight = controller;
      try {
        const result = await getConsultPrefill(consultantId, controller.signal);
        if (cancelled || settled || gen !== genRef.current) {
          return;
        }
        if (result) {
          settled = true;
          if (orderedRef.current.has(result.consultId)) {
            return; // already ordered -> no chip
          }
          // Consult-keying (ITAM-2614), same rule as the trigger poll: the
          // probe only runs from IDLE (where resetToIdle already cleared the
          // RFZO state AND the context draft), but a different consultId must
          // never inherit either.
          if (
            stateConsultIdRef.current != null &&
            stateConsultIdRef.current !== result.consultId
          ) {
            clearRfzoState();
          }
          if (
            ctxPrefillRef.current != null &&
            ctxPrefillRef.current.consultId !== result.consultId
          ) {
            clearDraft();
          }
          setPrefill(result);
          setPhase("CHIP"); // NEVER auto-open after refresh
        }
      } catch {
        // ignore; bounded window will end the probe
      } finally {
        busy = false;
        if (inFlight === controller) {
          inFlight = null;
        }
      }
    };

    probe();
    const interval = setInterval(() => {
      elapsed += MOUNT_PROBE_INTERVAL_MS;
      if (elapsed >= MOUNT_PROBE_MAX_MS) {
        cancelled = true;
        clearInterval(interval);
        if (inFlight) {
          inFlight.abort();
        }
        return;
      }
      probe();
    }, MOUNT_PROBE_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (inFlight) {
        inFlight.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, eligible, consultantId, armedConsult?.consultId]);

  const rfzoOrderable = rfzoResult?.orderable === true;

  // --- ITAM-2622 (3.6a): publish the unfinished-draft signal ---------------
  // Read the REACTIVE context prefill, never `ctxPrefillRef.current`: that ref
  // is assigned by a dependency-less effect, and writing a ref schedules no
  // render, so an effect keyed on it would be keyed on something that cannot
  // wake it — it could publish a stale value indefinitely. `ctx.prefill` comes
  // from the provider, so a hydrate/clearDraft re-renders this hook.
  //
  // `useLayoutEffect`, not `useEffect`, on ordering grounds: state changes that
  // move this boolean can originate in promise continuations (the submit paths
  // and the trigger-poll settle, which can clearDraft and change the phase in
  // one continuation), which React 18 batches into a commit whose PASSIVE
  // effects flush in a later macrotask. A layout effect flushes inside the
  // commit, closing a sub-frame gap on a control whose whole job is to be safe
  // at an arbitrary moment. It costs nothing else: the dispatch is a store
  // write rather than a render, and this hook does not subscribe to the field
  // it publishes, so there is no cascade.
  const unfinishedDraft = isConsultDraftUnfinished(
    phase,
    ctx.prefill != null,
    rfzoOrderable
  );
  useLayoutEffect(() => {
    // The dep is a primitive, so this runs only on a real change, and the
    // reducer assigns one boolean (immer no-ops when unchanged): no loop, no
    // dispatch-during-render.
    dispatch(consultOrderActions.publishUnfinishedDraft(unfinishedDraft));
  }, [dispatch, unfinishedDraft]);

  // --- Keep-alive poll (a modal/chip is holding a 200 prefill) -------------
  // RFZO working phases join the active set; a TERMINAL RFZO result behaves
  // like SUCCESS (excluded — consult end can never wipe the confirmation).
  useEffect(() => {
    const active =
      phase === "PROMPT" ||
      phase === "CHIP" ||
      phase === "FORM" ||
      phase === "SUBMITTING" ||
      phase === "RFZO_FORM" ||
      phase === "RFZO_SUBMITTING" ||
      (phase === "RFZO_RESULT" && rfzoOrderable);
    // Once the consult has ENDED (grace running), stop keep-alive entirely: the
    // grace timer alone owns the reset. Otherwise closing the form to the chip
    // during grace would restart keep-alive, hit the 404 threshold again, and
    // reset to IDLE — collapsing the grace window and wiping the draft early.
    // ITAM-2622: `eligible` gates the keep-alive for the same reason as the
    // trigger poll — it must never run for a session with no pharmacy context.
    if (!active || consultEnded || consultantId == null || !eligible) {
      return;
    }
    let cancelled = false;
    let busy = false; // in-flight guard: never overlap poll requests
    let inFlight: AbortController | null = null;
    const gen = ++genRef.current;
    keepAlive404Ref.current = 0;

    const tick = async () => {
      if (cancelled || busy) {
        return;
      }
      busy = true;
      const controller = new AbortController();
      inFlight = controller;
      try {
        const result = await getConsultPrefill(consultantId, controller.signal);
        if (cancelled || gen !== genRef.current) {
          return;
        }
        if (result) {
          keepAlive404Ref.current = 0; // still active
        } else {
          keepAlive404Ref.current += 1;
          if (keepAlive404Ref.current >= KEEPALIVE_404_THRESHOLD) {
            cancelled = true;
            clearInterval(interval);
            onConsultInactive();
          }
        }
      } catch {
        // transient error -> neither confirm nor count as a 404
      } finally {
        busy = false;
        if (inFlight === controller) {
          inFlight = null;
        }
      }
    };

    const interval = setInterval(tick, KEEPALIVE_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (inFlight) {
        inFlight.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phase,
    rfzoOrderable,
    consultEnded,
    consultantId,
    eligible,
    onConsultInactive,
  ]);

  // --- Marketplace / consultant change: full reset -------------------------
  const prevKeyRef = useRef(marketplaceKey);
  const prevConsultantRef = useRef(consultantId);
  useEffect(() => {
    if (
      prevKeyRef.current !== marketplaceKey ||
      prevConsultantRef.current !== consultantId
    ) {
      prevKeyRef.current = marketplaceKey;
      prevConsultantRef.current = consultantId;
      setOrderedConsultIds([]);
      resetToIdle();
    }
  }, [marketplaceKey, consultantId, resetToIdle]);

  // --- Unmount cleanup -----------------------------------------------------
  useEffect(
    () => () => {
      clearGraceTimer();
      genRef.current += 1;
      if (submitAbortRef.current) {
        submitAbortRef.current.abort();
      }
      // ITAM-2622 (3.6a): a derived signal cannot un-publish itself once its
      // deriver is gone. Without this, a persona remount (3.6) would leave the
      // last `true` standing and every later logout would warn about a draft
      // that no longer exists.
      dispatch(consultOrderActions.publishUnfinishedDraft(false));
    },
    [clearGraceTimer, dispatch]
  );

  // --- Actions -------------------------------------------------------------
  const openPrompt = useCallback(() => {
    setPhase((p) => (p === "CHIP" ? "PROMPT" : p));
  }, []);

  const dismissPrompt = useCallback(() => {
    setPhase((p) => (p === "PROMPT" ? "CHIP" : p));
  }, []);

  const confirmPrompt = useCallback(() => {
    if (!prefill) {
      return;
    }
    // Hydrate the draft only on the FIRST entry to the form for THIS consult.
    // Re-entering via the chip (CHIP -> PROMPT -> confirm) must PRESERVE the
    // pharmacist's edits; re-hydrating would reset the draft to the prefill and
    // silently discard added items / delivery choices.
    if (ctxPrefillRef.current?.consultId !== prefill.consultId) {
      hydrate(prefill);
    }
    // Invariant 3: the plain "Rezervacija" choice clears the RFZO order-mode
    // discriminator, so the CnC submit can never hit the RFZO endpoint. (The
    // prompt disables this choice while an orderable therapy exists — the
    // clear is defense in depth.)
    rfzoOrderTherapyIdRef.current = null;
    setRfzoOrderTherapyId(null);
    stateConsultIdRef.current = prefill.consultId;
    setCncFormVisited(true);
    setValidationErrors({});
    setSubmitError(null);
    setLineErrors({});
    setPhase("FORM");
  }, [prefill, hydrate]);

  const closeForm = useCallback(() => {
    // Preserve the draft; the chip reopens via the confirmation step. Leaving
    // the form also leaves RFZO order mode (re-entry goes through RFZO_RESULT,
    // which sets the discriminator again). Guarded on the phase so an Esc
    // during SUBMITTING can never strip the discriminator mid-flight.
    if (phaseRef.current === "FORM") {
      rfzoOrderTherapyIdRef.current = null;
      setRfzoOrderTherapyId(null);
    }
    setPhase((p) => (p === "FORM" ? "CHIP" : p));
  }, []);

  const closeSuccess = useCallback(() => {
    resetToIdle();
  }, [resetToIdle]);

  const dismissSubmitError = useCallback(() => {
    setSubmitError(null);
    setLineErrors({});
  }, []);

  const submit = useCallback(async () => {
    if (phaseRef.current === "SUBMITTING") {
      return; // in-flight guard
    }
    if (submitLockRef.current) {
      return; // synchronous re-entry lock (the phaseRef guard is effect-updated)
    }
    const draft = draftRef.current;
    const activePrefill = ctxPrefillRef.current;
    if (!activePrefill || !consultPharmacy) {
      return;
    }

    // ITAM-2614: a non-null discriminator routes this submit to the RFZO order
    // endpoint. Items + customer identity are read-only snapshots there (D11),
    // so validation skips them and keeps the delivery/address/personal-id rules.
    const rfzoTherapyId = rfzoOrderTherapyIdRef.current;
    const mode = rfzoTherapyId != null ? "RFZO_ORDER" : "CNC";

    const { valid, errors } = validateConsultOrderDraft(
      draft,
      activePrefill,
      consultPharmacyDetail,
      mode
    );
    if (!valid) {
      setValidationErrors(errors);
      // Bump the attempt nonce so the form focuses/scrolls to the first invalid
      // field (the button itself is never pre-disabled by validity anymore).
      setSubmitAttemptId((n) => n + 1);
      return;
    }
    submitLockRef.current = true; // set BEFORE any await
    setValidationErrors({});
    setSubmitError(null);
    setLineErrors({});
    setPhase("SUBMITTING");

    const controller = new AbortController();
    submitAbortRef.current = controller;
    try {
      if (rfzoTherapyId != null) {
        const payload = buildRfzoOrderPayload(draft, activePrefill);
        await orderRfzoTherapy(rfzoTherapyId, payload, controller.signal);
        if (controller.signal.aborted) {
          return;
        }
        clearGraceTimer(); // SUCCESS must never be wiped by the grace timer
        setCreatedOrder(null); // D7: 200 with an empty body — there is no order id
        setSuccessKind("RFZO");
        // rfzoOrderTherapyId/rfzoResult are intentionally KEPT: the still-open
        // modal derives its RFZO chrome (title, read-only sections) from them.
        // closeSuccess -> resetToIdle clears them.
        setOrderedConsultIds((prev) =>
          prev.includes(activePrefill.consultId)
            ? prev
            : [...prev, activePrefill.consultId]
        );
        setPhase("SUCCESS");
        return;
      }

      const payload = buildConsultOrderPayload(draft, activePrefill, {
        marketplaceId: consultPharmacy.marketplaceId,
        branchMarketplaceId: consultPharmacy.branchMarketplaceId,
      });
      const order = await createConsultOrder(payload, controller.signal);
      if (controller.signal.aborted) {
        return;
      }
      // The consult-end grace timer must not outlive a completed submit:
      // left armed, it would fire resetToIdle mid-SUCCESS and wipe the
      // confirmation (and created order number) out from under the pharmacist.
      clearGraceTimer();
      setCreatedOrder(order);
      setSuccessKind("CNC");
      setOrderedConsultIds((prev) =>
        prev.includes(activePrefill.consultId)
          ? prev
          : [...prev, activePrefill.consultId]
      );
      setPhase("SUCCESS");
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }
      if (rfzoTherapyId != null) {
        // No 409-as-success semantics here (CnC-specific). A step-2 404 means
        // the row is gone — most likely the order WAS created (retry after a
        // lost response); the mapped copy says so, and the board's poll shows
        // the order in Primljene. No automatic reconciliation (scope decision).
        const mapped = mapRfzoOrderError(error);
        setSubmitError(mapped.message);
        setPhase("FORM"); // keep the draft
        return;
      }
      const mapped = mapConsultSubmitError(error);
      if (mapped.is409) {
        // Already created -> treat as success (hide chip, mark ordered).
        clearGraceTimer(); // same as the success path: SUCCESS must not be wiped
        setOrderedConsultIds((prev) =>
          prev.includes(activePrefill.consultId)
            ? prev
            : [...prev, activePrefill.consultId]
        );
        setCreatedOrder(null);
        setSuccessKind("CNC");
        setSubmitError(mapped.message);
        setPhase("SUCCESS");
        return;
      }
      setSubmitError(mapped.message);
      if (mapped.lineErrors) {
        setLineErrors(mapped.lineErrors);
      }
      setPhase("FORM"); // keep the draft
    } finally {
      submitLockRef.current = false;
      submitAbortRef.current = null;
    }
  }, [consultPharmacy, consultPharmacyDetail, clearGraceTimer]);

  // --- ITAM-2614 RFZO actions ----------------------------------------------

  const confirmRfzo = useCallback(() => {
    if (!prefill) {
      return;
    }
    if (rfzoResultRef.current) {
      return; // a result exists -> the prompt routes to resumeRfzo instead
    }
    // Same once-per-consult hydration guard as confirmPrompt: both flows share
    // one "draft started" signal (ctx.prefill), so entering EITHER form seeds
    // BOTH drafts exactly once per consult and re-entry preserves edits.
    if (ctxPrefillRef.current?.consultId !== prefill.consultId) {
      hydrate(prefill);
    }
    stateConsultIdRef.current = prefill.consultId;
    setRfzoFormVisited(true);
    setRfzoValidationErrors({});
    setRfzoSubmitError(null);
    setPhase("RFZO_FORM");
  }, [prefill, hydrate]);

  const resumeRfzo = useCallback(() => {
    if (!rfzoResultRef.current) {
      return; // nothing to resume
    }
    setPhase((p) => (p === "PROMPT" ? "RFZO_RESULT" : p));
  }, []);

  const closeRfzoForm = useCallback(() => {
    // Preserve the draft; the chip reopens via the two-choice prompt.
    setPhase((p) => (p === "RFZO_FORM" ? "CHIP" : p));
  }, []);

  const closeRfzoResult = useCallback(() => {
    const result = rfzoResultRef.current;
    const terminal = result != null && result.orderable !== true;
    if (consultEnded && terminal) {
      // Consult over + terminal outcome: nothing left to do for this consult.
      resetToIdle();
      return;
    }
    // Orderable result (or live consult): back to the chip. During grace the
    // timer keeps running — the pharmacist can resume and finish the order
    // within the window. Deliberately does NOT mark the consult ordered: after
    // a due-later/no-content outcome a regular order stays possible (Q4).
    setPhase((p) => (p === "RFZO_RESULT" ? "CHIP" : p));
  }, [consultEnded, resetToIdle]);

  const submitRfzo = useCallback(
    async (mode: ConsultRfzoSubmitMode = "NORMAL") => {
      if (phaseRef.current === "RFZO_SUBMITTING") {
        return; // in-flight guard (belt)
      }
      if (submitLockRef.current) {
        return; // synchronous re-entry lock (suspenders — closes the same-tick window)
      }
      if (rfzoResultRef.current) {
        // A result already exists for this consult: pharmacist_create must not
        // be re-fired from this session (Q6 — every submission re-notifies the
        // patient). Correction happens from a fresh session.
        return;
      }
      const rfzoDraft = rfzoDraftRef.current;
      const activePrefill = ctxPrefillRef.current;
      if (!activePrefill || !consultPharmacy) {
        return;
      }

      const userEmail = activePrefill.user.username;
      const { valid, errors } = validateConsultRfzoDraft(
        rfzoDraft,
        userEmail,
        mode
      );
      if (!valid || !userEmail) {
        setRfzoValidationErrors(errors);
        setRfzoSubmitAttemptId((n) => n + 1);
        return;
      }
      submitLockRef.current = true; // set BEFORE any await
      setRfzoValidationErrors({});
      setRfzoSubmitError(null);
      setPhase("RFZO_SUBMITTING");

      const controller = new AbortController();
      submitAbortRef.current = controller;
      try {
        const payload = buildConsultRfzoCreatePayload(
          rfzoDraft,
          userEmail,
          {
            marketplaceId: consultPharmacy.marketplaceId,
            branchMarketplaceId: consultPharmacy.branchMarketplaceId,
          },
          mode
        );
        const result = await createPharmacistRfzoTherapy(
          payload,
          controller.signal
        );
        if (controller.signal.aborted) {
          return;
        }
        stateConsultIdRef.current = activePrefill.consultId;
        rfzoResultRef.current = result;
        setRfzoResult(result);
        // The result screen must know HOW the therapy was submitted: a
        // no-content outcome after an explicit "korisnik nema terapiju" is a
        // normal confirmation, while the same status after a NORMAL submit
        // means the backend could not map any entered item.
        setRfzoResultMode(mode);
        if (result.orderable !== true) {
          // Terminal outcome — mirror of the CnC success path: the grace timer
          // must never wipe the confirmation out from under the pharmacist.
          clearGraceTimer();
        }
        setPhase("RFZO_RESULT");
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        const mapped = mapRfzoCreateError(error, {
          lbo: rfzoDraft.lbo.trim(),
          userEmail: userEmail,
        });
        setRfzoSubmitError(mapped.message);
        if (mapped.fieldErrors) {
          setRfzoValidationErrors(mapped.fieldErrors);
          // A backend-attributed field error gets the same focus-first-invalid
          // treatment as client-side validation (the form keys focus on this
          // nonce).
          setRfzoSubmitAttemptId((n) => n + 1);
        }
        setPhase("RFZO_FORM"); // keep the draft
      } finally {
        submitLockRef.current = false;
        submitAbortRef.current = null;
      }
    },
    [consultPharmacy, clearGraceTimer]
  );

  const startRfzoOrder = useCallback(() => {
    const result = rfzoResultRef.current;
    if (
      phaseRef.current !== "RFZO_RESULT" ||
      !result ||
      result.orderable !== true
    ) {
      return;
    }
    if (!someRfzoItemInStock(result.items)) {
      return; // D9: the all-understock dead end — the result screen explains it
    }
    rfzoOrderTherapyIdRef.current = result.id;
    setRfzoOrderTherapyId(result.id);
    setValidationErrors({});
    setSubmitError(null);
    setLineErrors({});
    setPhase("FORM"); // the shared form modal renders in RFZO order mode
  }, []);

  const dismissRfzoSubmitError = useCallback(() => {
    setRfzoSubmitError(null);
  }, []);

  return useMemo(
    () => ({
      phase,
      prefill,
      chipVisible: phase === "CHIP",
      graceActive,
      consultEnded,
      createdOrder,
      submitError,
      validationErrors,
      submitAttemptId,
      lineErrors,
      rfzoResult,
      rfzoResultMode,
      rfzoValidationErrors,
      rfzoSubmitError,
      rfzoSubmitAttemptId,
      rfzoOrderTherapyId,
      successKind,
      cncFormVisited,
      rfzoFormVisited,
      openPrompt,
      dismissPrompt,
      confirmPrompt,
      closeForm,
      closeSuccess,
      submit,
      dismissSubmitError,
      confirmRfzo,
      resumeRfzo,
      closeRfzoForm,
      closeRfzoResult,
      submitRfzo,
      startRfzoOrder,
      dismissRfzoSubmitError,
    }),
    [
      phase,
      prefill,
      graceActive,
      consultEnded,
      createdOrder,
      submitError,
      validationErrors,
      submitAttemptId,
      lineErrors,
      rfzoResult,
      rfzoResultMode,
      rfzoValidationErrors,
      rfzoSubmitError,
      rfzoSubmitAttemptId,
      rfzoOrderTherapyId,
      successKind,
      cncFormVisited,
      rfzoFormVisited,
      openPrompt,
      dismissPrompt,
      confirmPrompt,
      closeForm,
      closeSuccess,
      submit,
      dismissSubmitError,
      confirmRfzo,
      resumeRfzo,
      closeRfzoForm,
      closeRfzoResult,
      submitRfzo,
      startRfzoOrder,
      dismissRfzoSubmitError,
    ]
  );
};
